/**
 * sc-header-collapse — shopping pages only (collection + search templates;
 * loaded from sections/header-group-new.liquid). While the shopper is
 * actively scrolling, the fixed header (.c-nav) slides up out of view; when
 * scrolling stops it slides back. The sticky filter bar (.category_section)
 * follows via --sc-hc-offset so it sits at the top of the viewport while the
 * header is hidden and just below the header when it is shown.
 *
 * The header never hides near the top of the page, while it has keyboard
 * focus (:focus-visible) or a mouse pointer over it (desktop mega menu), or
 * while the mobile menu, search bar, predictive search, a cart drawer, the
 * filter drawer or a modal is open. CSS lives in assets/sc-header-collapse.css.
 */
(function () {
  'use strict';

  var IDLE_MS = 200; // scroll idle before the header returns
  var MIN_DELTA = 6; // px of travel before a scroll counts (iOS toolbar jitter)

  var nav = document.querySelector('.navbar-2 .c-nav');
  if (!nav || !('requestAnimationFrame' in window)) return;

  var root = document.documentElement;
  var body = document.body;
  var hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  var hidden = false;
  var headerH = nav.offsetHeight;
  var anchorY = window.scrollY;
  var idleTimer = 0;
  var ticking = false;

  function setHeight(h) {
    h = Math.round(h);
    if (!h || h === headerH && root.style.getPropertyValue('--sc-header-h')) return;
    headerH = h;
    root.style.setProperty('--sc-header-h', h + 'px');
  }

  // Keyboard focus only: a tap leaves focus on the menu/cart/search button,
  // which must not pin the header open on phones.
  function keyboardFocusInside() {
    var el = document.activeElement;
    if (!el || !nav.contains(el)) return false;
    try {
      return el.matches(':focus-visible');
    } catch (e) {
      return true;
    }
  }

  function isVisible(el) {
    return !!el && el.offsetHeight > 0 && getComputedStyle(el).display !== 'none';
  }

  // Anything open that the header must stay put for.
  function blocked() {
    if (keyboardFocusInside()) return true;
    if (hoverCapable.matches && nav.matches(':hover')) return true;
    if (
      body.classList.contains('overflow-hidden') ||
      body.classList.contains('overflow-hidden-mobile') ||
      body.classList.contains('overflow-hidden-tablet') ||
      root.classList.contains('sc-cart-drawer-mobile-locked')
    ) {
      return true;
    }
    if (
      document.querySelector(
        '.navbar-2 .w-nav-button.w--open, .navbar-2 [data-nav-menu-open],' +
          ' predictive-search[open], sc-cart-drawer-mobile.is-open,' +
          ' details.mobile-facets__disclosure[open], .menu-opening,' +
          ' .sc-sidebar__view--category-filter.active, dialog[open], modal-dialog[open]'
      )
    ) {
      return true;
    }
    if (isVisible(nav.querySelector('.search-bar-wrapper'))) return true;
    var cart = document.querySelector('.w-commerce-commercecartcontainerwrapper');
    if (cart && cart.style.display && cart.style.display !== 'none') return true;
    return false;
  }

  function apply(next) {
    if (next === hidden) return;
    hidden = next;
    body.classList.toggle('sc-hc-hidden', hidden);
  }

  function show() {
    anchorY = window.scrollY;
    apply(false);
  }

  function onFrame() {
    ticking = false;
    var y = window.scrollY;
    if (y <= headerH || blocked()) {
      show();
      return;
    }
    if (!hidden && Math.abs(y - anchorY) < MIN_DELTA) return;
    apply(true);
  }

  function onScroll() {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(show, IDLE_MS);
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(onFrame);
    }
  }

  setHeight(headerH);
  if ('ResizeObserver' in window) {
    new ResizeObserver(function (entries) {
      var box = entries[0].borderBoxSize && entries[0].borderBoxSize[0];
      setHeight(box ? box.blockSize : nav.offsetHeight);
    }).observe(nav);
  } else {
    window.addEventListener('resize', function () {
      setHeight(nav.offsetHeight);
    });
  }

  // Keyboard focus or a pointer entering the header brings it straight back.
  nav.addEventListener('focusin', function () {
    if (keyboardFocusInside()) show();
  });
  nav.addEventListener('pointerenter', function (e) {
    if (e.pointerType === 'mouse') show();
  });
  window.addEventListener('scroll', onScroll, { passive: true });
  body.classList.add('sc-hc');
})();
